import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { Network } from '@capacitor/network';
import { SYNC_INTERVAL } from './api.service';

export enum SyncStatusStates {
  initializing = 'Initializing',
  synchronizing = 'Synchronizing',
  synchronized = 'Synchronized',
  error = 'Error',
  unauthorized = 'Unauthorized',
}

export abstract class SyncStatusService {
  public syncSuccess$: Observable<boolean>;
  public syncStatus$ = new BehaviorSubject<SyncStatusStates>(
    SyncStatusStates.initializing,
  );

  private _online = false;
  protected _syncTimeoutHandle: ReturnType<typeof setTimeout>;
  protected _lastSync: string;
  private _destroyed = false; // Add flag to track destruction

  public constructor() {
    Network.getStatus().then((status) => (this._online = status.connected));
    Network.addListener('networkStatusChange', async (status) => {
      this._online = status.connected;
      if (!this._online) {
        this.syncStatus$.next(SyncStatusStates.error);
      } else {
        if (this.syncStatus$.getValue() === SyncStatusStates.error) {
          // just came back online so force a sync right now
          await this._synchronizeNow();
        }
      }
    });
    this.syncSuccess$ = this.syncStatus$.asObservable().pipe(
      map((status) => {
        const success = status === SyncStatusStates.synchronized;
        return success;
      }),
      distinctUntilChanged(),
    );
  }

  public synchronizing(): void {
    if (this.syncStatus$.getValue() !== SyncStatusStates.error) {
      // stay in error state until synchronized
      this.syncStatus$.next(SyncStatusStates.synchronizing);
    }
  }

  public synchronized(): void {
    this.syncStatus$.next(SyncStatusStates.synchronized);
  }

  public syncError(): void {
    this.syncStatus$.next(SyncStatusStates.error);
  }

  public syncUnauthorized(): void {
    this.syncStatus$.next(SyncStatusStates.unauthorized);
  }

  public async liveSync(): Promise<void> {
    // Add check to prevent execution after destruction
    if (this._destroyed) {
      return;
    }

    // add incremental updates from the server
    try {
      // called from settimeout or manually so clear any existing timeout
      this._clearSyncTimeout();
      this.synchronizing();
      const docs = await this._getLatestFromApi();
      docs.forEach((payload) => this._addUpdate(payload));
      this._publish();
      this.synchronized();
    } catch (e) {
      console.error(e);
      if (e.status === 401) {
        this.syncUnauthorized();
      } else {
        this.syncError();
      }
    } finally {
      // Only schedule refresh if not destroyed
      if (!this._destroyed) {
        this._scheduleRefresh();
      }
    }
  }

  public abstract load(force: boolean): Promise<unknown>;

  protected abstract _getLatestFromApi(): Promise<Array<unknown>>;

  protected abstract _addUpdate(payload: unknown): void;

  protected abstract _publish(): void;

  protected _clearSyncTimeout(): void {
    if (this._syncTimeoutHandle) {
      clearTimeout(this._syncTimeoutHandle);
      this._syncTimeoutHandle = null;
    }
  }

  protected async _synchronizeNow(): Promise<void> {
    if (this._destroyed) {
      return;
    }

    try {
      this._clearSyncTimeout();
      // force a full reload from the server
      await this.load(true);
      this.synchronized();
    } catch (e) {
      console.error(e);
    } finally {
      if (!this._destroyed) {
        this._scheduleRefresh();
      }
    }
  }

  private _scheduleRefresh() {
    if (!this._destroyed) {
      this._syncTimeoutHandle = setTimeout(
        () => this.liveSync(),
        SYNC_INTERVAL,
      );
    }
  }

  // Add public destroy method that concrete services can call in ngOnDestroy
  public destroy(): void {
    this._destroyed = true;
    this._clearSyncTimeout();
    this.syncStatus$.complete();
  }
}
